import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Modal } from 'react-bootstrap';
import { matchPath } from 'react-router';
import { Icon } from '@homeexchange/design';
import i18n from '../../../i18n';
import Utils from '../../../utils/utils';
import Screen from '../../../utils/Screen';
import Api from '../../../api/Api';
import Analytics from '../../../utils/analytics';

import InputNumber from '../../form/InputNumber';
import Alert from '../../common/alert/Alert';
import InputRangeCalendar from '../../calendar/InputRangeCalendar';

import ConfirmModificationModal from './ConfirmModificationModal';
import ModificationSent from './ModificationSent';

class GpExchangePopup extends React.PureComponent {
    static propTypes = {
        firstName: PropTypes.string.isRequired,
        convExchange: PropTypes.object.isRequired,
        convId: PropTypes.number.isRequired,
        availabilities: PropTypes.array.isRequired,
        closeModificationFormModal: PropTypes.func.isRequired,
        updateLoadingState: PropTypes.func.isRequired,
        showModificationFormModal: PropTypes.bool,
        isCollection: PropTypes.bool.isRequired,
        userTotalGp: PropTypes.number.isRequired,
        fetchConversation: PropTypes.func.isRequired
    };

    constructor(props) {
        super(props);

        this.state = {
            dateRange: {
                from: null,
                to: null
            },
            oldDateRange: {
                from: null,
                to: null
            },
            nbGuests: 0,
            oldNbGuests: 0,
            nbGuestPoints: 0,
            disabledModifyButton: false,
            fieldsUpdated: [],
            modificationRequestOnGoing: false,
            shouldShowExchangeModificationForm: true,
            showConfirmationModal: false,
            gpAmount: null,
            shouldSetSubAutoRenew: false,
            shouldBuyGp: false
        };

        this.handleGuestsNbChange = this.handleGuestsNbChange.bind(this);
        this.setPeriod = this.setPeriod.bind(this);
        this.modifyExchange = this.modifyExchange.bind(this);
        this.closeModificationFormModal = this.closeModificationFormModal.bind(this);
        this.requestExchangeModification = this.requestExchangeModification.bind(this);
        this.toggleConfirmationModal = this.toggleConfirmationModal.bind(this);
        this.getModificationData = this.getModificationData.bind(this);
        this.getGPBalance = this.getGPBalance.bind(this);
    }

    componentDidMount() {
        const { convExchange } = this.props;
        this.setState({
            nbGuests: convExchange?.get('guest_nb'),
            oldNbGuests: convExchange?.get('guest_nb'),
            nbGuestPoints: convExchange?.get('guestpoint_amount'),
            dateRange: {
                from: moment(convExchange?.get('start_on')),
                to: moment(convExchange?.get('end_on'))
            },
            oldDateRange: {
                from: moment(convExchange?.get('start_on')),
                to: moment(convExchange?.get('end_on'))
            }
        });
    }

    toggleConfirmationModal() {
        this.setState({
            showConfirmationModal: !this.state.showConfirmationModal
        });
    }

    handleGuestsNbChange(e) {
        const value = parseInt(e.target.value, 10);
        const element = 'nbGuest';

        this.setState({ nbGuests: value });

        if (!this.state.fieldsUpdated.includes(element)) {
            this.setState((prevState) => ({
                fieldsUpdated: [...prevState.fieldsUpdated, element]
            }));
        }

        if (value === this.state.oldNbGuests) {
            this.setState({
                fieldsUpdated: this.state.fieldsUpdated.filter(function (field) {
                    return field !== element;
                })
            });
        }
    }

    setPeriod(period) {
        const element = 'period';

        if (period.length < 2) {
            this.setState({
                disabledModifyButton: true
            });
            return;
        }

        if (!this.state.fieldsUpdated.includes(element)) {
            this.setState((prevState) => ({
                fieldsUpdated: [...prevState.fieldsUpdated, element]
            }));
        }

        const startOn = moment(period[0], 'YYYY-MM-DD');
        const endOn = moment(period[1], 'YYYY-MM-DD');
        const newDateRange = {
            from: moment.isMoment(period[0]) ? startOn : null,
            to: moment.isMoment(period[1]) ? endOn : null
        };
        const nights = endOn.diff(startOn, 'days', true);

        if (
            startOn.format('YYYY-MM-DD') === moment(this.state.oldDateRange.from).format('YYYY-MM-DD') &&
            endOn.format('YYYY-MM-DD') === moment(this.state.oldDateRange.to).format('YYYY-MM-DD')
        ) {
            this.setState({
                fieldsUpdated: this.state.fieldsUpdated.filter(function (field) {
                    return field !== element;
                })
            });
        }

        this.setState({
            dateRange: newDateRange,
            nbGuestPoints: nights * this.props.convExchange?.get('guestpoint_amount'),
            disabledModifyButton: false
        });
    }

    getModificationData() {
        const { dateRange, nbGuests, nbGuestPoints } = this.state;

        return [
            {
                start_on: dateRange.from.format('YYYY-MM-DD'),
                end_on: dateRange.to.format('YYYY-MM-DD'),
                guest_nb: nbGuests,
                guestpoint_amount: nbGuestPoints,
                host_id: this.props.convExchange?.get('host').get('id')
            }
        ];
    }

    requestExchangeModification() {
        this.setState({
            modificationRequestOnGoing: true
        });

        const data = this.getModificationData();

        Api.Exchange.requestExchangeModification(this.props.convId, data).then(
            (response) => {
                const {
                    should_buy_gp: shouldBuyGp,
                    should_set_sub_autorenew: shouldSetSubAutoRenew,
                    gp_amount: gpAmount
                } = response;

                this.setState({ shouldBuyGp, shouldSetSubAutoRenew, gpAmount });

                if (shouldBuyGp || shouldSetSubAutoRenew) {
                    this.closeModificationFormModal();
                    this.toggleConfirmationModal();
                } else {
                    this.modifyExchange();
                }

                Analytics.trackGTM('exchange_status', {
                    event_data: {
                        exchange_status: 'finalized',
                        modification_status: 'pending',
                        exchange_type: 'guest_point_exchange',
                        collection: this.props.isCollection
                    }
                });
            },
            (errors) => {
                console.error(`[EXCHANGE MODIFICATION]: ${errors}`);
            }
        );
    }

    modifyExchange(callback = null) {
        const exchangeData = this.getModificationData();

        Api.Exchange.modifyExchange(this.props.convId, exchangeData).then(() => {
            if (callback) {
                callback();
            } else {
                this.props.updateLoadingState();
                this.closeModificationFormModal();
            }

            sweetAlert({
                content: (
                    <ModificationSent
                        confirmCallback={() => {
                            sweetAlert.close();

                            this.props.fetchConversation(this.props.convId).then(() => {
                                if (matchPath('/:locale?/conversations/:id/infos', location.pathname)) {
                                    document.dispatchEvent(
                                        new CustomEvent('request_card', {
                                            detail: {
                                                view: 'conversation'
                                            }
                                        })
                                    );
                                }
                            });
                        }}
                    />
                ),
                closeOnClickOutside: false,
                button: false,
                className: 'modify-exchange-modal'
            });
        });
    }

    closeModificationFormModal() {
        this.props.closeModificationFormModal();
        this.setState({ modificationRequestOnGoing: false });
        this.props.updateLoadingState();
    }

    getGPBalance() {
        if (!this.state.gpAmount) {
            return null;
        }
        return this.state.gpAmount - this.props.userTotalGp;
    }

    render() {
        const { firstName, convExchange } = this.props;
        const { nbGuests, disabledModifyButton, modificationRequestOnGoing } = this.state;
        const hasModification = this.state.fieldsUpdated.length === 0;

        return (
            <>
                <Modal show={this.props.showModificationFormModal} dialogClassName="modify-exchange-modal">
                    <div className="modify-exchange-modal-content">
                        <div className="modify-modal-header">
                            <Icon
                                id="close-modal-gp-exchange"
                                name="Cross"
                                onClick={this.closeModificationFormModal}
                            />
                            <h3 className="edit-title">
                                {i18n.t('exchange:modify-exchange.edit.modal.title')}
                            </h3>
                            <Alert
                                content={i18n.t('exchange:modify-exchange.edit.modal.warning.text')}
                                color="warning"
                                icon={<Icon name="InfoCircleOutline" />}
                            />
                        </div>
                        <div className="modify-modal-body">
                            <p className="receiver-name">
                                {i18n.t('exchange:user_exchange', {
                                    first_name: Utils.ucfirst(firstName)
                                })}
                            </p>
                            <label className="input-field-label">
                                {i18n.t('exchange:modify-exchange.exchange-dates.label')}
                            </label>
                            <InputRangeCalendar
                                mode="range"
                                classname={['search-calendar', 'hends-calendar-gp-exchange']}
                                classNames="hends-calendar"
                                nbMonths={Screen.isMobile() ? 1 : 2}
                                startOn={moment(convExchange?.get('start_on'), 'YYYY-MM-DD')}
                                endOn={moment(convExchange?.get('end_on'), 'YYYY-MM-DD')}
                                selectedHandler={this.setPeriod}
                                availabilities={this.props.availabilities}
                            />
                            <div className="guests-nb-input form-group">
                                <label className="input-field-label" htmlFor="guests_nb">
                                    <span>{Utils.ucfirst(i18n.t('exchange:guest_nb'))}</span>
                                </label>
                                <InputNumber
                                    id="guests_nb"
                                    name="detail.guests_nb"
                                    value={nbGuests || convExchange?.get('guest_nb') || '0'}
                                    onChange={this.handleGuestsNbChange}
                                    min={1}
                                    max={parseInt(convExchange?.get('home_capacity'), 10) || 0}
                                />
                            </div>
                            <div className="gp-per-night">
                                <label className="input-field-label">
                                    <span>{i18n.t('home:gp_per_night')}</span>
                                </label>
                                <p>
                                    {i18n.t('common:exchange.gp-amount', {
                                        gpAmount: convExchange?.get('guestpoint_amount')
                                    })}
                                </p>
                            </div>
                            <button
                                id="submit-modify-gp-exchange"
                                className={classNames('btn btn-primary send-request-btn btn-ajax', {
                                    sending: modificationRequestOnGoing
                                })}
                                onClick={this.requestExchangeModification}
                                disabled={hasModification || disabledModifyButton}
                            >
                                {i18n.t('exchange:contact.send_request')}
                            </button>
                            <div className="modify-modal-footer">
                                <p
                                    className="modify-modal-footer-text"
                                    dangerouslySetInnerHTML={{
                                        __html: i18n.t('exchange:modify-exchange.edit.footer.link')
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </Modal>

                <Modal show={this.state.showConfirmationModal} dialogClassName="modify-exchange-modal">
                    <ConfirmModificationModal
                        displayAutoRenewActivation={this.state.shouldSetSubAutoRenew}
                        displayGpPurchase={this.state.shouldBuyGp}
                        modifyExchange={this.modifyExchange}
                        closeConfirmationModal={this.toggleConfirmationModal}
                        gpAmount={this.getGPBalance()}
                    />
                </Modal>
            </>
        );
    }
}

export default GpExchangePopup;
